import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mqttApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { Mono, StatusDot } from "@/components/Atoms";
import { Plus, Trash2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "@/lib/rbac";

export default function MqttTopics() {
  const qc = useQueryClient();
  const canWrite = useCan("write:mqtt");
  const list = useQuery({ queryKey: ["mqtt-topics"], queryFn: mqttApi.list });
  const [modal, setModal] = useState(null);
  const inval = () => qc.invalidateQueries({ queryKey: ["mqtt-topics"] });
  const cM = useMutation({ mutationFn: mqttApi.create, onSuccess: () => { toast.success("Topic added"); inval(); setModal(null); } });
  const uM = useMutation({ mutationFn: ({ id, data }) => mqttApi.update(id, data), onSuccess: () => { inval(); } });
  const dM = useMutation({ mutationFn: mqttApi.remove, onSuccess: () => { toast.success("Topic deleted"); inval(); } });
  const reload = useMutation({ mutationFn: mqttApi.reload, onSuccess: (r) => toast.success(`Topics reloaded at ${new Date(r.reloaded_at).toLocaleTimeString()}`) });
  const pager = usePager(list.data || [], { defaultSize: 50, sizes: [25, 50, 100] });

  return (
    <PageContainer>
      <PageHeader
        title="MQTT topics"
        subtitle="Dynamic subscription set. Reload to push to the broker."
        actions={
          canWrite ? (
            <div className="flex gap-2">
              <button data-testid="mqtt-reload" onClick={() => reload.mutate()} className="border border-zinc-200 bg-white rounded-md px-3 py-2 text-sm font-medium hover:bg-zinc-50 inline-flex items-center gap-1.5"><RefreshCcw className="w-4 h-4" /> Reload</button>
              <button data-testid="mqtt-new" onClick={() => setModal({ topic: "", qos: 0, enabled: true })} className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> New topic</button>
            </div>
          ) : null
        }
      />
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left p-3 font-semibold">Topic</th>
              <th className="text-left p-3 font-semibold">QoS</th>
              <th className="text-left p-3 font-semibold">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pager.pageRows.map((t) => (
              <tr key={t.id} className="border-t border-zinc-100">
                <td className="p-3"><Mono>{t.topic}</Mono></td>
                <td className="p-3 font-mono">{t.qos}</td>
                <td className="p-3">
                  {canWrite ? (
                    <button onClick={() => uM.mutate({ id: t.id, data: { enabled: !t.enabled } })}>
                      <StatusDot online={t.enabled} label={t.enabled ? "enabled" : "paused"} />
                    </button>
                  ) : (
                    <StatusDot online={t.enabled} label={t.enabled ? "enabled" : "paused"} />
                  )}
                </td>
                <td className="p-3 text-right">
                  {canWrite && (
                    <button onClick={() => { if (window.confirm("Delete topic?")) dM.mutate(t.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
            {pager.total === 0 && (
              <tr><td colSpan={4} className="py-10 text-center text-sm text-zinc-500">No topics yet.</td></tr>
            )}
          </tbody>
        </table>
        <Pager {...pager} testidPrefix="mqtt-pager" />
      </div>
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3">New topic</h2>
            <form onSubmit={(e) => { e.preventDefault(); cM.mutate(modal); }} className="space-y-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">topic</span>
                <input data-testid="mqtt-input-topic" className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono" value={modal.topic} onChange={(e) => setModal({ ...modal, topic: e.target.value })} placeholder="iot/+/up" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">qos</span>
                <select className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white" value={modal.qos} onChange={(e) => setModal({ ...modal, qos: +e.target.value })}>
                  <option value={0}>0</option><option value={1}>1</option><option value={2}>2</option>
                </select>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(null)} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm">Cancel</button>
                <button data-testid="mqtt-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
